import { Modal } from "react-bootstrap";
import { KPI_TYPE_EDIT_NOTE } from "./KpiTypeChangeDialog";

const FIELD_LABELS = {
  title: "Title",
  moduleId: "Module",
  type: "Type",
  aggregation: "Aggregation",
  frequency: "Frequency",
  units: "Units",
  tagGroups: "Tags",
  evidences: "Evidence",
  kpiTypes: "KPI type",
  category: "Category",
  defaultDescription: "Default description",
  yearDescription: "This year description",
  isReadOnly: "Read only",
  applicableCheck: "Applicable check",
  applicableNote: "Applicable note",
  trigerCheck: "Trigger check",
  active: "Active",
};

const SHOW_MAX = 100;

/**
 * Review of an Excel upload before anything is saved, then the outcome.
 * Apply stays disabled while the file has any error. The save runs in batches,
 * each its own transaction, so a failure part-way leaves earlier batches saved.
 *
 * @param {object} props
 * @param {object|null} props.plan from planImport, plus `fileName`
 * @param {{ created: number, updated: number, factorKpis: number }|null} props.result
 * @param {string} [props.error]
 * @param {string} [props.busy]
 * @param {string} props.financialYearLabel
 * @param {() => void} props.onApply
 * @param {() => void} props.onClose
 */
export default function ImportDialog({ plan, result, error, busy, financialYearLabel, onApply, onClose }) {
  const show = !!(plan || result || (error && !busy));
  if (!show) return null;

  const errors = plan?.errors || [];
  const canApply = plan && !errors.length && plan.hasChanges && !busy;
  const kpiTypeEdits = (plan?.updates || []).filter((u) => u.fields.includes("kpiTypes")).length;

  return (
    <Modal show onHide={busy ? undefined : onClose} size="lg" centered scrollable className="kgb-root kgb-modal">
      <Modal.Header closeButton={!busy}>
        <Modal.Title as="h5">{plan ? `Review ${plan.fileName}` : result ? "Upload saved" : "Excel"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="kgb-import">
        {plan ? (
          <>
            <p className="kgb-import-fy">Financial year {financialYearLabel}</p>
            <div className="kgb-import-stats">
              <Stat label="New KPIs" value={plan.creates.length} />
              <Stat label="KPIs updated" value={plan.updates.length} />
              <Stat label="KPIs with factor changes" value={plan.factorItems.length} />
              <Stat label="New tag pairs" value={plan.newKeywords.length} />
              <Stat label="Unchanged" value={plan.unchanged} muted />
            </div>

            {errors.length ? (
              <section>
                <h6 className="kgb-import-errors-title">
                  {errors.length} problem{errors.length === 1 ? "" : "s"} — fix them in the file and upload again
                </h6>
                <table className="kgb-import-table">
                  <tbody>
                    {errors.slice(0, SHOW_MAX).map((e, i) => (
                      <tr key={i}>
                        <td>{e.sheet}</td>
                        <td>{e.line ? `Row ${e.line}` : ""}</td>
                        <td>{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {errors.length > SHOW_MAX ? <p className="kgb-muted">and {errors.length - SHOW_MAX} more</p> : null}
              </section>
            ) : null}

            {!errors.length && plan.creates.length ? (
              <section>
                <h6>New KPIs</h6>
                <ul className="kgb-import-list">
                  {plan.creates.slice(0, SHOW_MAX).map((c) => (
                    <li key={c.line}>{c.values.title}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {!errors.length && plan.updates.length ? (
              <section>
                <h6>Updated KPIs</h6>
                {kpiTypeEdits ? (
                  <p className="kgb-import-warning">
                    {kpiTypeEdits} KPI{kpiTypeEdits === 1 ? "" : "s"} change KPI type. {KPI_TYPE_EDIT_NOTE}
                  </p>
                ) : null}
                <ul className="kgb-import-list">
                  {plan.updates.slice(0, SHOW_MAX).map((u) => (
                    <li key={u.line}>
                      {u.row.title} <span className="kgb-muted">— {u.fields.map((f) => FIELD_LABELS[f] || f).join(", ")}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {!errors.length && plan.newKeywords.length ? (
              <section>
                <h6>New tag pairs</h6>
                <ul className="kgb-import-list">
                  {plan.newKeywords.slice(0, SHOW_MAX).map((k) => (
                    <li key={`${k.keywordType}|${k.keywordValue}`}>
                      <span className="kgb-muted">{k.keywordType}:</span> {k.keywordValue}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {!errors.length && plan.factorItems.length ? (
              <section>
                <h6>Emission factors</h6>
                <ul className="kgb-import-list">
                  {plan.factorItems.slice(0, SHOW_MAX).map((f) => (
                    <li key={`${f.questionId}-${f.title}`}>
                      {f.title} <span className="kgb-muted">— {f.before} → {f.after} factors</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {!errors.length && !plan.hasChanges ? <p>Nothing to change — the file matches what is saved.</p> : null}
          </>
        ) : null}

        {result ? (
          <p>
            Saved {result.created} new KPI{result.created === 1 ? "" : "s"}, updated {result.updated}, emission
            factors on {result.factorKpis}, and added {result.keywords} tag pair
            {result.keywords === 1 ? "" : "s"}.
          </p>
        ) : null}

        {error ? <p className="kgb-import-error">{error}</p> : null}
        {busy ? <p className="kgb-muted">{busy}</p> : null}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="kgb-btn" disabled={!!busy} onClick={onClose}>
          {plan ? "Cancel" : "Close"}
        </button>
        {plan ? (
          <button type="button" className="kgb-btn kgb-btn--primary" disabled={!canApply} onClick={onApply}>
            {busy || "Apply changes"}
          </button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}

function Stat({ label, value, muted }) {
  return (
    <div className={`kgb-import-stat${muted ? " kgb-import-stat--muted" : ""}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}
